/**
 * usePortalContainer — Où poser un portail pour qu'il reste visible en plein écran
 *
 * En plein écran, le navigateur ne peint que le sous-arbre de
 * `document.fullscreenElement` (couche supérieure) : un portail accroché à
 * `document.body` — liste d'un `AgAutocomplete`, menu, infobulle — existe dans
 * le DOM mais n'apparaît jamais à l'écran.
 *
 * Le conteneur retourné est donc l'élément plein écran dès que l'ancre s'y
 * trouve, et `document.body` sinon. Le choix est réévalué à chaque changement
 * de plein écran, pour qu'un survol en cours suive le passage d'un mode à
 * l'autre.
 *
 * La spécification Fullscreen impose `transform: none` à l'élément plein écran :
 * un enfant en `position: fixed` y reste positionné par rapport au viewport,
 * les coordonnées calculées via `getBoundingClientRect()` restent valables.
 */

import { useLayoutEffect, useState, type RefObject } from 'react';

/** Ancre du portail : l'élément lui-même ou la ref qui le porte */
export type PortalAnchor = Element | null | undefined | RefObject<Element | null>;

const resolveAnchor = (anchor: PortalAnchor): Element | null => {
  if (!anchor) return null;
  return 'current' in anchor ? anchor.current : anchor;
};

const resolveContainer = (anchor: PortalAnchor): HTMLElement => {
  const fullscreen = document.fullscreenElement;
  const element = resolveAnchor(anchor);
  if (fullscreen instanceof HTMLElement && element && fullscreen.contains(element)) {
    return fullscreen;
  }
  return document.body;
};

/**
 * @param anchor  Élément (ou ref) auquel le contenu portalisé est rattaché
 * @param active  `true` quand le portail est monté ; le calcul est inutile sinon
 */
export function usePortalContainer(anchor: PortalAnchor, active = true): HTMLElement {
  const [container, setContainer] = useState<HTMLElement>(() => document.body);

  const anchorElement = resolveAnchor(anchor);

  // `useLayoutEffect` : le conteneur est arrêté avant la peinture, sinon le
  // flottant apparaîtrait une image dans `body` — donc invisible en plein écran.
  useLayoutEffect(() => {
    if (!active) return;
    const sync = () => setContainer(resolveContainer(anchor));
    sync();
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
    // `anchor` peut être une ref stable dont seul `current` change : on suit
    // l'élément résolu plutôt que l'objet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorElement, active]);

  return container;
}

export default usePortalContainer;
